//#region IMPORTS
import {
  setSelectedInstoreOrders,
  bulkUpdateInstoreOrderStatusAsync,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import {
  CONFIRM_BULK_INSTORE_ORDERS,
  BULK_CONFIRM_THRESHOLD,
} from "../../models/_consts";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { downloadFile } from "../../helpers/downloadFile";
import { useState } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import MainModal from "../_layout/MainModal";
import AreYouSure from "../modalTypes/AreYouSure";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  // "export" only offers the export button (status-filtered overview).
  mode: "approve" | "in-progress" | "ship" | "export";
}
//#endregion

export default function InstoreOrdersBulkActions({ mode }: Props) {
  const dispatch = useAppDispatch();
  const selected = useAppSelector(
    (state) => state.instoreOrders.selectedInstoreOrders,
  );

  // Confirmation modal for the bulk actions
  const [pendingAction, setPendingAction] = useState<{
    message: string;
    run: () => void;
  } | null>(null);

  // Only ask for confirmation on larger batches; small ones run immediately.
  const requestConfirm = (count: number, message: string, run: () => void) => {
    if (count > BULK_CONFIRM_THRESHOLD) {
      setPendingAction({ message, run });
      dispatch(openModal(CONFIRM_BULK_INSTORE_ORDERS));
    } else {
      run();
    }
  };

  const closeConfirm = () => {
    setPendingAction(null);
    dispatch(openModal(false));
  };

  const count = selected.length;
  const plural = count === 1 ? "" : "s";
  const label =
    mode === "approve"
      ? count > 0
        ? `Approve ${count} order${plural}`
        : "Approve multiple"
      : mode === "in-progress"
        ? count > 0
          ? `Set ${count} in progress`
          : "Set in progress"
        : count > 0
          ? `Ship ${count} order${plural}`
          : "Ship order(s)";

  const exportLabel =
    selected.length > 0
      ? `Export ${selected.length} order${selected.length === 1 ? "" : "s"}`
      : "Export multiple";

  const handleClick = async () => {
    // "export" has no status-change primary action.
    if (mode === "export" || selected.length === 0) return;
    await dispatch(
      bulkUpdateInstoreOrderStatusAsync({
        ids: selected,
        mode,
      }),
    );
    dispatch(setSelectedInstoreOrders([]));
    // The thunk refetched the (now filtered) list; InstoreOrdersView auto-
    // advances to the next flow pill if it emptied.
  };

  const handleExport = async () => {
    if (selected.length === 0) return;
    try {
      const response = await toast.promise(
        agent.InstoreOrders.exportMany(selected),
        {
          pending: "Generating export data",
          success: "Download succeeded",
          error: "Something went wrong",
        },
      );

      const blobFromFetch = new Blob([response], { type: "application/zip" });

      downloadFile({
        blobFromFetch,
        extension: "zip",
        fileName: `InstoreOrders__${format(new Date(), "dd-MM-yyyy")}`,
      });
    } catch {
      // toast.promise already surfaced the error
    }
  };

  const orderWord = selected.length === 1 ? "order" : "orders";
  const actionVerb =
    mode === "approve"
      ? "Approve"
      : mode === "in-progress"
        ? "Set in progress"
        : "Ship";

  return (
    <div className="flex gap-4 items-center">
      {/* Export multiple — first, available on the manage + send workflows */}
      {mode !== "approve" && (
        <button
          type="button"
          onClick={() =>
            requestConfirm(
              selected.length,
              `Export ${selected.length} ${orderWord} to a ZIP?`,
              handleExport,
            )
          }
          disabled={selected.length === 0}
          className="inline-flex items-center rounded bg-sky-900 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-sky-900"
        >
          {exportLabel}
        </button>
      )}

      {mode !== "export" && (
        <button
          type="button"
          onClick={() =>
            requestConfirm(
              selected.length,
              `${actionVerb} ${selected.length} ${orderWord}?`,
              handleClick,
            )
          }
          disabled={selected.length === 0}
          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
        >
          {label}
        </button>
      )}

      {/* Confirmation modal for the bulk actions */}
      <MainModal type={CONFIRM_BULK_INSTORE_ORDERS} closeHandler={closeConfirm}>
        <AreYouSure
          closeHandler={closeConfirm}
          confirmHandler={() => {
            pendingAction?.run();
            closeConfirm();
          }}
          title="Confirm bulk action"
          message={pendingAction?.message}
        />
      </MainModal>
    </div>
  );
}
